import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { IconBuildingStore, IconInbox, IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MatrixCardList } from '../../components/Matrix/MatrixCardList.js';
import { MatrixGrid } from '../../components/Matrix/MatrixGrid.js';
import { GAME_ROWS } from '../../components/Matrix/rowSpecs.js';
import { gridChrome } from '../../components/Matrix/gridChrome.js';
import { PullFailedAlert } from '../../components/Matrix/PullFailedAlert.js';
import { MatrixLoadState } from '../../components/Matrix/MatrixLoadState.js';
import { MatrixOverlays } from '../../components/Matrix/MatrixOverlays.js';
import { MatrixPageActions } from '../../components/Matrix/MatrixPageActions.js';
import { MobileFilters } from '../../components/Matrix/MobileFilters.js';
import { EMPTY_FILTER, isFiltered, visibleColumns } from '../../domain/rowFilter.js';
import { useRowFilter } from '../../components/Matrix/useRowFilter.js';
import { PageContainer } from '../../components/PageContainer/PageContainer.js';
import { StudioSelect } from '../../components/StudioSelect/StudioSelect.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { useMatrixPageState } from '../../hooks/useMatrixPageState.js';
import { useTRPC } from '../../utils/trpc.js';
import { newGameRoute } from '../../domain/routes.js';
import { emptyReason, listTotal, masterCount } from '../../domain/emptyReason.js';
import { MatrixEmptyState } from '../../components/Matrix/MatrixEmptyState.js';
import { studioLabelOf } from '../../domain/studioLabel.js';
import { gamesFact, useStudioFilter } from './gamesFilter.js';
import { gamesInboxSentence, gamesInboxSources, type InboxSource } from './gamesInbox.js';
import { StudioRail } from './StudioRail.js';
import { useGameRows } from '../../hooks/useGameRows.js';
import { useStudioRows } from '../../hooks/useStudioRows.js';

/**
 * The Games page: every master game across studios, or one studio's when `?studio=` is set.
 *
 * One kind of row, paged 50 at a time. Every filter lives in the URL — `?studio=` here, search,
 * segment and status through `useRowFilter` — so any of them can be linked to. Selection is this page's own:
 * leaving drops it, because pushing a selection you can no longer see is how you push the
 * wrong thing.
 */
export const GamesPage = () => {
    const trpc = useTRPC();
    const isMobile = useIsMobile();
    const { studio, setStudio } = useStudioFilter();

    const { filters, patch, clear: clearFilters } = useRowFilter();
    const matrix = useMatrixPageState();
    const { selection, setDrawer, pulls } = matrix;

    // `filters.search` is already the debounced value: `DebouncedSearchInput`/`MobileFilters`
    // only patch the URL 300ms after the last keystroke, so reading `filters` straight off the
    // URL here does not need its own debounce on top (that used to double it to ~600ms).
    const games = useGameRows(studio, filters);
    const options = useQuery(trpc.studios.options.queryOptions());
    // The rail's own query (page 1, no filter) — same key, so this is the cached read, not a
    // second request. `childCount` and `title` come from here instead of a per-page `studios.get`;
    // a studio not on that first page just hides the count.
    const studioRows = useStudioRows(EMPTY_FILTER);
    const studioRow = studio ? studioRows.rows.find((r) => r.key === studio) : undefined;

    const columns = games.columns;
    // The environment filter focuses the grid on one column; the toolbar still offers them all.
    // Memoised: `visibleColumns` returns a fresh array whenever `environment` narrows to one
    // column, and `MatrixGrid`'s `columns` prop feeds every memoised `MatrixRow`, so a new
    // reference here on every render would defeat that memo just as surely as an unstable
    // callback would.
    const shownColumns = useMemo(() => visibleColumns(columns, filters.environment), [columns, filters.environment]);
    const rows = games.rows;
    const environmentNames = columns.map((c) => c.name);
    const filtered = isFiltered(filters);
    const studioLabel = studioLabelOf(options.data, studio, studioRow?.title ?? null);
    const total = games.isSuccess
        ? listTotal({
              loaded: rows.length,
              hasMore: games.hasMore,
              filtered,
              known: studio ? (studioRow?.childCount ?? null) : masterCount(columns, 'game'),
          })
        : null;
    // With a studio chosen but its row not on the rail's first page, no roll-ups means no
    // banner — never the catalogue-wide counts under a drawer narrowed to one studio.
    const reason = emptyReason(columns, filtered, studio);
    const inboxSources =
        games.isSuccess && reason !== 'master_empty'
            ? gamesInboxSources(columns, studio ? (studioRow?.rollups ?? {}) : null)
            : [];
    const paging = {
        total,
        filtered,
        hasMore: games.hasMore,
        isLoadingMore: games.isLoadingMore,
        onLoadMore: games.loadMore,
    };

    const chooseStudio = (value: string | null) => {
        selection.clear();
        setStudio(value);
    };

    // The phone's chooser; above `sm` the rail left of the grid is, never both.
    const studioSelect = (
        <StudioSelect
            w={isMobile ? '100%' : 220}
            placeholder="Any studio"
            aria-label="Studio"
            leftSection={<IconBuildingStore size={15} stroke={1.7} />}
            options={options.data ?? []}
            value={studio}
            onChange={chooseStudio}
            clearable
        />
    );

    // Memoised so an unrelated re-render (a keystroke, a selection toggle, another query
    // settling) does not hand `PageContainer` a new `actions` element every time — `patch` is
    // now a genuinely stable `useCallback` (`useUrlFilter`) so it is listed like any other dep;
    // `pulls` is stable in behaviour though not always in reference, so only its own primitives
    // are listed instead.
    const actions = useMemo(
        () => (
            <MatrixPageActions
                isMobile={isMobile}
                searchPlaceholder="Find a game"
                search={filters.search}
                onSearchChange={(search) => patch({ search })}
                newButton={
                    <Button
                        component={Link}
                        to={newGameRoute(studio)}
                        leftSection={<IconPlus size={15} stroke={1.7} />}
                    >
                        New game
                    </Button>
                }
                pulling={pulls.pullingAll}
                onPullAll={() => pulls.pullAll(environmentNames)}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `pulls` is stable in behaviour, not reference
        [isMobile, filters.search, studio, patch, pulls.pullingAll, environmentNames.join(',')],
    );

    return (
        <PageContainer
            title="Games"
            count={games.isSuccess ? gamesFact(total, studioLabel?.label ?? null) : undefined}
            actions={actions}
        >
            <Group align="flex-start" wrap="nowrap" gap="md">
                {!isMobile && <StudioRail selected={studio} onSelect={chooseStudio} />}
                <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
                    {isMobile && studioSelect}
                    {isMobile && <MobileFilters value={filters} onChange={patch} placeholder="Find a game" />}

                    <PullFailedAlert columns={columns} />
                    <GamesInboxAlert sources={inboxSources} onOpen={matrix.setInbox} />

                    <MatrixLoadState
                        isLoading={games.isLoading}
                        isError={games.isError && rows.length === 0}
                        error={games.error}
                        onRetry={games.refetch}
                        label="games"
                    />

                    {games.isSuccess && rows.length === 0 && (
                        <MatrixEmptyState
                            type="game"
                            reason={reason}
                            columns={columns}
                            onClearFilters={clearFilters}
                            parent={studioLabel}
                            onNew={{ label: 'New game', to: newGameRoute(studio) }}
                            onAdopt={({ environment, count }) =>
                                matrix.adoption.adopt({ environment, count, scope: 'all' })
                            }
                            adopting={matrix.adoption.isPending}
                        />
                    )}

                    {rows.length > 0 &&
                        (isMobile ? (
                            <MatrixCardList
                                spec={GAME_ROWS}
                                columns={shownColumns}
                                rows={rows}
                                selection={selection}
                                onOpen={setDrawer}
                                paging={paging}
                            />
                        ) : (
                            <MatrixGrid
                                spec={GAME_ROWS}
                                columns={shownColumns}
                                rows={rows}
                                selection={selection}
                                onOpen={setDrawer}
                                paging={paging}
                                {...gridChrome(matrix, filters, patch, columns, rows)}
                            />
                        ))}
                </Stack>
            </Group>

            <MatrixOverlays
                state={matrix}
                environments={environmentNames}
                inboxStudio={studio}
                // The page is filtered to a studio that no longer exists: drop the filter.
                onDeleted={(deleted) => {
                    if (deleted.some((d) => d.type === 'studio' && d.key === studio)) setStudio(null);
                }}
            />
        </PageContainer>
    );
};

/**
 * Games an environment holds and master does not: never grid rows, so they are announced here
 * with a way into each environment's inbox, where they are adopted.
 */
const GamesInboxAlert = ({ sources, onOpen }: { sources: InboxSource[]; onOpen: (environment: string) => void }) =>
    sources.length > 0 && (
        <Alert color="blue" icon={<IconInbox size={16} stroke={1.7} />} p="sm" role="status">
            <Group gap="sm" wrap="wrap" justify="space-between">
                <Text fz="sm">{gamesInboxSentence(sources)}</Text>
                <Group gap="xs">
                    {sources.map(({ environment, count }) => (
                        <Button key={environment} size="xs" variant="light" onClick={() => onOpen(environment)}>
                            Adopt {count} from {environment}
                        </Button>
                    ))}
                </Group>
            </Group>
        </Alert>
    );
